/**
 * Data Center Cooling Network — 1D Navier-Stokes thermohydraulic model.
 *
 * Two hydraulically independent loops coupled thermally through plate heat exchangers:
 *
 *   PRIMARY LOOP (heat rejection side):
 *     Expansion tank (pressure anchor) → 3× primary pumps (2 duty, 1 standby)
 *     → primary supply header → 3× plate HX (primary side) → primary return header
 *     → heat rejection sink → back to expansion tank
 *
 *   SECONDARY LOOP (IT cooling side):
 *     3× secondary pumps (2 duty, 1 standby) → secondary supply header
 *     → 8× parallel rack cooling branches (valve + CDU + return pipe)
 *     → secondary return header → 3× plate HX (secondary side) → back to pumps
 *
 * Thermal coupling: at each solver step, Q_transfer is computed from both sides'
 * temperatures using ε-NTU method, then applied as energy source/sink to each HX component.
 *
 * All parameters are configurable via CoolingNetworkConfig — nothing is hardcoded.
 *
 * Reference conditions (design point):
 *   Total IT load: 60 MW (8 × 7.5 MW)
 *   Primary: 15°C supply / 25°C return, ~1430 L/s total (2 duty pumps)
 *   Secondary: 18°C supply / 28°C return, ~1430 L/s total (2 duty pumps)
 *   Plate HX: UA sized for 60 MW at 10°C LMTD
 */

import type { NSSolverState, SIMPLEConfig } from '../types';
import { DEFAULT_SIMPLE_CONFIG } from '../types';
import { createPipe } from '../network/nsPipe';
import { createJunction } from '../network/nsJunction';
import { createNSSolverState } from '../network/nsNetwork';
import { NSPump } from '../components/nsPump';
import { NSHeatExchanger } from '../components/nsHeatExchanger';
import { propsFromPT_SI, propsFromPH_SI } from '../core/fluidAdapter';

// ─── Configuration ───────────────────────────────────────────────────────────

export interface CoolingNetworkConfig {
  // IT Load
  numRackBranches: number;           // Number of parallel rack cooling branches
  rackHeatLoad_W: number;            // Heat load per rack branch [W]

  // Primary loop
  numPrimaryPumps: number;           // Total primary pumps (duty + standby)
  numPrimaryPumpsDuty: number;       // Number running at design
  primaryPumpHead_m: number;         // Rated pump head [m]
  primaryPumpFlow_kgs: number;       // Rated pump flow per pump [kg/s]
  primaryPumpInertia_kgm2: number;   // Pump moment of inertia [kg·m²]
  primaryPumpEfficiency: number;     // Pump hydraulic efficiency
  primarySupplyTemp_C: number;       // Design supply temperature [°C]
  primaryReturnTemp_C: number;       // Design return temperature [°C]
  primaryHeaderDiameter_m: number;   // Primary header pipe diameter [m]
  primaryHeaderLength_m: number;     // Primary header run length [m]

  // Heat rejection (primary side boundary)
  numHeatRejectionUnits: number;     // Number of chillers / dry coolers
  heatRejectionCapacity_W: number;   // Total heat rejection capacity [W]

  // Plate heat exchangers
  numPlateHX: number;                // Number of plate heat exchangers
  plateHX_UA_WK: number;            // UA per heat exchanger [W/K]

  // Secondary loop
  numSecondaryPumps: number;
  numSecondaryPumpsDuty: number;
  secondaryPumpHead_m: number;
  secondaryPumpFlow_kgs: number;
  secondaryPumpInertia_kgm2: number;
  secondaryPumpEfficiency: number;
  secondarySupplyTemp_C: number;
  secondaryReturnTemp_C: number;
  secondaryHeaderDiameter_m: number;
  secondaryHeaderLength_m: number;

  // Rack branch piping
  rackBranchDiameter_m: number;
  rackBranchLength_m: number;

  // Pipe parameters
  pipeRoughness_m: number;
  pipeNcells: number;                // Cells per pipe segment

  // Fluid
  operatingPressure_Pa: number;      // System static pressure
}

export const DEFAULT_COOLING_CONFIG: CoolingNetworkConfig = {
  numRackBranches: 8,
  rackHeatLoad_W: 7.5e6,            // 7.5 MW per branch

  numPrimaryPumps: 3,
  numPrimaryPumpsDuty: 2,
  primaryPumpHead_m: 20,             // ~200 kPa at 998 kg/m³
  primaryPumpFlow_kgs: 715,          // Per pump: total ~1430 kg/s for 2 duty
  primaryPumpInertia_kgm2: 25,       // Sized for ~15-20s coast-down
  primaryPumpEfficiency: 0.82,
  primarySupplyTemp_C: 15,
  primaryReturnTemp_C: 25,
  primaryHeaderDiameter_m: 0.5,      // DN500
  primaryHeaderLength_m: 80,

  numHeatRejectionUnits: 3,
  heatRejectionCapacity_W: 60e6,     // 60 MW total

  numPlateHX: 3,
  plateHX_UA_WK: 2e6,               // 2 MW/K per HX → 60MW at 10°C LMTD

  numSecondaryPumps: 3,
  numSecondaryPumpsDuty: 2,
  secondaryPumpHead_m: 20,
  secondaryPumpFlow_kgs: 715,
  secondaryPumpInertia_kgm2: 25,
  secondaryPumpEfficiency: 0.82,
  secondarySupplyTemp_C: 18,
  secondaryReturnTemp_C: 28,
  secondaryHeaderDiameter_m: 0.4,    // DN400
  secondaryHeaderLength_m: 60,

  rackBranchDiameter_m: 0.125,       // DN125
  rackBranchLength_m: 20,

  pipeRoughness_m: 0.00005,          // Commercial steel
  pipeNcells: 3,

  operatingPressure_Pa: 400000,      // 4 bar (pressurised system)
};

// SIMPLE config tuned for liquid water cooling loops
const COOLING_SIMPLE_CONFIG: SIMPLEConfig = {
  ...DEFAULT_SIMPLE_CONFIG,
  maxOuterIterations: 40,
  pressureRelaxation: 0.15,
  velocityRelaxation: 0.4,
  enthalpyRelaxation: 0.5,
};

// ─── Network Builder ─────────────────────────────────────────────────────────

/**
 * Enthalpy of liquid water at given T (°C) and P (Pa).
 * Falls back to cp approximation if IAPWS fails.
 */
function waterEnthalpy(T_C: number, P_Pa: number): number {
  try {
    const props = propsFromPT_SI(P_Pa, T_C + 273.15);
    return props.h;
  } catch {
    // Approximate: h ≈ cp × T for liquid water
    return 4186 * T_C;
  }
}

// Pipe wall thermal mass constants (carbon steel)
const STEEL_RHO_CP = 3.56e6; // J/(m³·K) — ρ=7850 kg/m³ × cp=454 J/(kg·K)

/** Estimate pipe wall thickness from diameter using Schedule 40 approximation. */
function schedule40WallThickness(D_m: number): number {
  // Schedule 40 wall thickness approximation (m)
  if (D_m <= 0.05) return 0.0038;    // DN40
  if (D_m <= 0.1) return 0.006;      // DN80-100
  if (D_m <= 0.15) return 0.007;     // DN125-150
  if (D_m <= 0.25) return 0.0093;    // DN200-250
  if (D_m <= 0.4) return 0.0109;     // DN300-400
  return 0.0127;                      // DN500+
}

/**
 * Create an initialized pipe for the cooling network.
 * Includes pipe wall thermal mass for transient thermal inertia.
 */
function makeCoolingPipe(
  id: string,
  diameter: number,
  length: number,
  P_Pa: number,
  T_C: number,
  mdot: number,
  nCells: number,
  roughness: number,
): ReturnType<typeof createPipe> {
  const h = waterEnthalpy(T_C, P_Pa);
  const pipe = createPipe(id, diameter, length, roughness, 0, nCells, P_Pa, T_C + 273.15, mdot);

  // Override with correct enthalpy-based initialisation
  const T_K = T_C + 273.15;
  for (let i = 0; i < pipe.nCells; i++) {
    pipe.cells[i].P = P_Pa;
    pipe.cells[i].h = h;
    try {
      const props = propsFromPT_SI(P_Pa, T_K);
      pipe.cells[i].rho = props.rho;
      pipe.cells[i].T = props.T;
      pipe.cells[i].c = props.c;
      pipe.cells[i].mu = props.mu;
      pipe.cells[i].cp = props.cp;
      pipe.cells[i].x = props.x;
      pipe.cells[i].phase = props.phase;
      pipe.cells[i].u_int = props.u_int;
    } catch { /* keep defaults */ }
    Object.assign(pipe.cells_old[i], pipe.cells[i]);
  }

  // Add pipe wall thermal mass
  pipe.wallThickness = schedule40WallThickness(diameter);
  pipe.wallRhoCp = STEEL_RHO_CP;
  pipe.T_wall = new Array(pipe.nCells).fill(T_K);
  pipe.T_wall_old = new Array(pipe.nCells).fill(T_K);

  return pipe;
}

/**
 * Create the DC cooling network NS solver state.
 *
 * Network topology uses splitters/mixers modeled as multi-connection junctions.
 * Parallel paths (pumps, HXs, rack branches) are each separate pipe segments
 * connected at split/merge junctions.
 */
export function createCoolingNetworkNS(
  config: CoolingNetworkConfig = DEFAULT_COOLING_CONFIG,
  simpleConfig: SIMPLEConfig = COOLING_SIMPLE_CONFIG,
): NSSolverState {
  const pipes = new Map();
  const junctions = new Map();
  const components = new Map();

  const P = config.operatingPressure_Pa;
  const nc = config.pipeNcells;
  const rough = config.pipeRoughness_m;
  const Dp = config.primaryHeaderDiameter_m;
  const Ds = config.secondaryHeaderDiameter_m;
  const Dr = config.rackBranchDiameter_m;

  // Design flow rates
  const totalPrimaryFlow = config.primaryPumpFlow_kgs * config.numPrimaryPumpsDuty;
  const totalSecondaryFlow = config.secondaryPumpFlow_kgs * config.numSecondaryPumpsDuty;
  const flowPerHX_primary = totalPrimaryFlow / config.numPlateHX;
  const flowPerHX_secondary = totalSecondaryFlow / config.numPlateHX;
  const flowPerBranch = totalSecondaryFlow / config.numRackBranches;

  // Enthalpies at design temperatures
  const h_pri_supply = waterEnthalpy(config.primarySupplyTemp_C, P);
  const h_pri_return = waterEnthalpy(config.primaryReturnTemp_C, P);
  const h_sec_supply = waterEnthalpy(config.secondarySupplyTemp_C, P);
  const h_sec_return = waterEnthalpy(config.secondaryReturnTemp_C, P);

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIMARY LOOP
  // ═══════════════════════════════════════════════════════════════════════════

  // --- Primary pumps (parallel) ---
  for (let i = 0; i < config.numPrimaryPumps; i++) {
    const isDuty = i < config.numPrimaryPumpsDuty;
    const pumpId = `pri_pump_${i}`;
    const pipeIn = `p_pri_pump_in_${i}`;
    const pipeOut = `p_pri_pump_out_${i}`;
    const speed = isDuty ? 1.0 : 0.0;

    pipes.set(pipeIn, makeCoolingPipe(pipeIn, Dp, 5, P, config.primaryReturnTemp_C,
      isDuty ? config.primaryPumpFlow_kgs : 0, nc, rough));
    pipes.set(pipeOut, makeCoolingPipe(pipeOut, Dp, 5, P + config.primaryPumpHead_m * 998 * 9.81,
      config.primarySupplyTemp_C, isDuty ? config.primaryPumpFlow_kgs : 0, nc, rough));

    components.set(pumpId, new NSPump(pumpId, {
      mode: 'dynamic-speed',
      ratedHead: config.primaryPumpHead_m,
      ratedFlow: config.primaryPumpFlow_kgs,
      ratedSpeed: 1480,
      speed,
      efficiency: config.primaryPumpEfficiency,
      inertia: config.primaryPumpInertia_kgm2,
      rho: 998,
      D: Dp,
    }));

    junctions.set(`j_pri_pump_${i}`, createJunction(`j_pri_pump_${i}`, 'component',
      [{ pipeId: pipeIn, faceIndex: nc, direction: 'inflow' },
       { pipeId: pipeOut, faceIndex: 0, direction: 'outflow' }],
      P, h_pri_return, pumpId));
  }

  // Primary pump split/merge headers
  const priPumpSplitConns = [
    { pipeId: 'p_pri_return_to_pumps', faceIndex: nc, direction: 'inflow' as const },
    ...Array.from({ length: config.numPrimaryPumps }, (_, i) =>
      ({ pipeId: `p_pri_pump_in_${i}`, faceIndex: 0, direction: 'outflow' as const })),
  ];
  junctions.set('j_pri_pump_split', createJunction('j_pri_pump_split', 'split',
    priPumpSplitConns, P, h_pri_return));

  const priPumpMergeConns = [
    ...Array.from({ length: config.numPrimaryPumps }, (_, i) =>
      ({ pipeId: `p_pri_pump_out_${i}`, faceIndex: nc, direction: 'inflow' as const })),
    { pipeId: 'p_pri_supply_header', faceIndex: 0, direction: 'outflow' as const },
  ];
  junctions.set('j_pri_pump_merge', createJunction('j_pri_pump_merge', 'merge',
    priPumpMergeConns, P, h_pri_supply));

  // Primary supply header (pumps → HX split)
  pipes.set('p_pri_supply_header', makeCoolingPipe('p_pri_supply_header', Dp,
    config.primaryHeaderLength_m / 2, P, config.primarySupplyTemp_C, totalPrimaryFlow, nc + 2, rough));

  // --- Primary side of plate HXs (parallel) ---
  const priHxSplitConns = [
    { pipeId: 'p_pri_supply_header', faceIndex: nc + 2, direction: 'inflow' as const },
    ...Array.from({ length: config.numPlateHX }, (_, i) =>
      ({ pipeId: `p_pri_hx_in_${i}`, faceIndex: 0, direction: 'outflow' as const })),
  ];
  junctions.set('j_pri_hx_split', createJunction('j_pri_hx_split', 'split',
    priHxSplitConns, P, h_pri_supply));

  for (let i = 0; i < config.numPlateHX; i++) {
    const hxId = `pri_hx_${i}`;
    const pipeIn = `p_pri_hx_in_${i}`;
    const pipeOut = `p_pri_hx_out_${i}`;

    pipes.set(pipeIn, makeCoolingPipe(pipeIn, Dp * 0.7, 3, P, config.primarySupplyTemp_C,
      flowPerHX_primary, nc, rough));
    pipes.set(pipeOut, makeCoolingPipe(pipeOut, Dp * 0.7, 3, P, config.primaryReturnTemp_C,
      flowPerHX_primary, nc, rough));

    // Primary side absorbs heat from secondary (positive Q = heat into primary)
    const designQ = config.rackHeatLoad_W * config.numRackBranches / config.numPlateHX;
    // COMPONENT HYDRAULIC SIZING (Fix Wave W1): the HX K-loss is now LIVE and
    // flow-dependent, so the flow-passage D must be a real bore — the old
    // dangling default (D = 0.2 m, K = 10) demanded −658 kPa at design flow
    // against a 196 kPa pump head and railed the IAPWS pressure floor.
    // Plate-pack effective passage taken as the loop header bore (port-header
    // scale); K = 6 is a typical plate-HX pack + port loss at design velocity
    // (VDI Heat Atlas / vendor ranges 4-8).
    components.set(hxId, new NSHeatExchanger(hxId, {
      Q_rate: designQ,
      UA: config.plateHX_UA_WK,
      hxType: 'counter',
      K_loss: 6,
      D: Dp,
    }));

    junctions.set(`j_pri_hx_${i}`, createJunction(`j_pri_hx_${i}`, 'component',
      [{ pipeId: pipeIn, faceIndex: nc, direction: 'inflow' },
       { pipeId: pipeOut, faceIndex: 0, direction: 'outflow' }],
      P, h_pri_supply, hxId));
  }

  // Primary HX merge → return header
  const priHxMergeConns = [
    ...Array.from({ length: config.numPlateHX }, (_, i) =>
      ({ pipeId: `p_pri_hx_out_${i}`, faceIndex: nc, direction: 'inflow' as const })),
    { pipeId: 'p_pri_return_header', faceIndex: 0, direction: 'outflow' as const },
  ];
  junctions.set('j_pri_hx_merge', createJunction('j_pri_hx_merge', 'merge',
    priHxMergeConns, P, h_pri_return));

  // Primary return header (HX merge → heat rejection)
  pipes.set('p_pri_return_header', makeCoolingPipe('p_pri_return_header', Dp,
    config.primaryHeaderLength_m / 2, P, config.primaryReturnTemp_C, totalPrimaryFlow, nc + 2, rough));

  // --- Heat rejection (modeled as energy sink boundary) ---
  // Removes heat from primary return, producing cooled supply water.
  // Capacity must exceed IT load by pump work (~1-2 MW) to close energy balance.
  // Q_reject = -(IT_load + pump_work_primary + pump_work_secondary)
  const pumpWorkPrimary = totalPrimaryFlow * config.primaryPumpHead_m * 9.81 / config.primaryPumpEfficiency;
  const pumpWorkSecondary = totalSecondaryFlow * config.secondaryPumpHead_m * 9.81 / config.secondaryPumpEfficiency;
  const heatRejectionQ = -(config.heatRejectionCapacity_W + pumpWorkPrimary + pumpWorkSecondary);
  // Header-bank passage at the primary header bore; K = 3 is a typical
  // dry-cooler / tube-bank header loss at design velocity (see the plate-HX
  // sizing note above for why a real bore is now REQUIRED).
  components.set('heat_rejection', new NSHeatExchanger('heat_rejection', {
    Q_rate: heatRejectionQ,
    K_loss: 3,
    D: Dp,
  }));

  pipes.set('p_pri_hr_to_tank', makeCoolingPipe('p_pri_hr_to_tank', Dp, 20,
    P, config.primarySupplyTemp_C, totalPrimaryFlow, nc, rough));

  junctions.set('j_heat_rejection', createJunction('j_heat_rejection', 'component',
    [{ pipeId: 'p_pri_return_header', faceIndex: nc + 2, direction: 'inflow' },
     { pipeId: 'p_pri_hr_to_tank', faceIndex: 0, direction: 'outflow' }],
    P, h_pri_return, 'heat_rejection'));

  // Primary pressure anchor (expansion tank)
  pipes.set('p_pri_return_to_pumps', makeCoolingPipe('p_pri_return_to_pumps', Dp, 10,
    P, config.primarySupplyTemp_C, totalPrimaryFlow, nc, rough));

  junctions.set('j_pri_tank', createJunction('j_pri_tank', 'boundary',
    [{ pipeId: 'p_pri_hr_to_tank', faceIndex: nc, direction: 'inflow' },
     { pipeId: 'p_pri_return_to_pumps', faceIndex: 0, direction: 'outflow' }],
    P, h_pri_supply));

  // ═══════════════════════════════════════════════════════════════════════════
  // SECONDARY LOOP
  // ═══════════════════════════════════════════════════════════════════════════

  // --- Secondary pumps (parallel) ---
  for (let i = 0; i < config.numSecondaryPumps; i++) {
    const isDuty = i < config.numSecondaryPumpsDuty;
    const pumpId = `sec_pump_${i}`;
    const pipeIn = `p_sec_pump_in_${i}`;
    const pipeOut = `p_sec_pump_out_${i}`;

    pipes.set(pipeIn, makeCoolingPipe(pipeIn, Ds, 5, P, config.secondarySupplyTemp_C,
      isDuty ? config.secondaryPumpFlow_kgs : 0, nc, rough));
    pipes.set(pipeOut, makeCoolingPipe(pipeOut, Ds, 5, P + config.secondaryPumpHead_m * 998 * 9.81,
      config.secondarySupplyTemp_C, isDuty ? config.secondaryPumpFlow_kgs : 0, nc, rough));

    components.set(pumpId, new NSPump(pumpId, {
      mode: 'dynamic-speed',
      ratedHead: config.secondaryPumpHead_m,
      ratedFlow: config.secondaryPumpFlow_kgs,
      ratedSpeed: 1480,
      speed: isDuty ? 1.0 : 0.0,
      efficiency: config.secondaryPumpEfficiency,
      inertia: config.secondaryPumpInertia_kgm2,
      rho: 998,
      D: Ds,
    }));

    junctions.set(`j_sec_pump_${i}`, createJunction(`j_sec_pump_${i}`, 'component',
      [{ pipeId: pipeIn, faceIndex: nc, direction: 'inflow' },
       { pipeId: pipeOut, faceIndex: 0, direction: 'outflow' }],
      P, h_sec_supply, pumpId));
  }

  // Secondary pump split/merge
  const secPumpSplitConns = [
    { pipeId: 'p_sec_hx_to_pumps', faceIndex: nc, direction: 'inflow' as const },
    ...Array.from({ length: config.numSecondaryPumps }, (_, i) =>
      ({ pipeId: `p_sec_pump_in_${i}`, faceIndex: 0, direction: 'outflow' as const })),
  ];
  junctions.set('j_sec_pump_split', createJunction('j_sec_pump_split', 'split',
    secPumpSplitConns, P, h_sec_supply));

  const secPumpMergeConns = [
    ...Array.from({ length: config.numSecondaryPumps }, (_, i) =>
      ({ pipeId: `p_sec_pump_out_${i}`, faceIndex: nc, direction: 'inflow' as const })),
    { pipeId: 'p_sec_supply_header', faceIndex: 0, direction: 'outflow' as const },
  ];
  junctions.set('j_sec_pump_merge', createJunction('j_sec_pump_merge', 'merge',
    secPumpMergeConns, P, h_sec_supply));

  // Secondary supply header
  pipes.set('p_sec_supply_header', makeCoolingPipe('p_sec_supply_header', Ds,
    config.secondaryHeaderLength_m, P, config.secondarySupplyTemp_C, totalSecondaryFlow, nc + 2, rough));

  // --- Rack branches (parallel) ---
  const rackSplitConns = [
    { pipeId: 'p_sec_supply_header', faceIndex: nc + 2, direction: 'inflow' as const },
    ...Array.from({ length: config.numRackBranches }, (_, i) =>
      ({ pipeId: `p_rack_supply_${i}`, faceIndex: 0, direction: 'outflow' as const })),
  ];
  junctions.set('j_rack_split', createJunction('j_rack_split', 'split',
    rackSplitConns, P, h_sec_supply));

  for (let i = 0; i < config.numRackBranches; i++) {
    const cduId = `rack_cdu_${i}`;
    const supplyPipe = `p_rack_supply_${i}`;
    const returnPipe = `p_rack_return_${i}`;

    // Supply pipe (includes valve loss via roughness/minor loss)
    pipes.set(supplyPipe, makeCoolingPipe(supplyPipe, Dr, config.rackBranchLength_m / 2,
      P, config.secondarySupplyTemp_C, flowPerBranch, nc, rough));

    // Return pipe
    pipes.set(returnPipe, makeCoolingPipe(returnPipe, Dr, config.rackBranchLength_m / 2,
      P, config.secondaryReturnTemp_C, flowPerBranch, nc, rough));

    // CDU / rack heat exchanger — applies IT heat load to the coolant.
    // Passage at the rack-branch bore; K = 4 for a compact CDU HX + couplings
    // (see the plate-HX sizing note above for why a real bore is REQUIRED).
    components.set(cduId, new NSHeatExchanger(cduId, {
      Q_rate: config.rackHeatLoad_W,
      K_loss: 4,
      D: Dr,
    }));

    junctions.set(`j_rack_cdu_${i}`, createJunction(`j_rack_cdu_${i}`, 'component',
      [{ pipeId: supplyPipe, faceIndex: nc, direction: 'inflow' },
       { pipeId: returnPipe, faceIndex: 0, direction: 'outflow' }],
      P, h_sec_supply, cduId));
  }

  // Rack merge → secondary return header
  const rackMergeConns = [
    ...Array.from({ length: config.numRackBranches }, (_, i) =>
      ({ pipeId: `p_rack_return_${i}`, faceIndex: nc, direction: 'inflow' as const })),
    { pipeId: 'p_sec_return_header', faceIndex: 0, direction: 'outflow' as const },
  ];
  junctions.set('j_rack_merge', createJunction('j_rack_merge', 'merge',
    rackMergeConns, P, h_sec_return));

  // Secondary return header
  pipes.set('p_sec_return_header', makeCoolingPipe('p_sec_return_header', Ds,
    config.secondaryHeaderLength_m, P, config.secondaryReturnTemp_C, totalSecondaryFlow, nc + 2, rough));

  // --- Secondary side of plate HXs (parallel) ---
  // Note: j_sec_hx_split junction is created below after the return anchor boundary.

  for (let i = 0; i < config.numPlateHX; i++) {
    const hxId = `sec_hx_${i}`;
    const pipeIn = `p_sec_hx_in_${i}`;
    const pipeOut = `p_sec_hx_out_${i}`;

    pipes.set(pipeIn, makeCoolingPipe(pipeIn, Ds * 0.7, 3, P, config.secondaryReturnTemp_C,
      flowPerHX_secondary, nc, rough));
    pipes.set(pipeOut, makeCoolingPipe(pipeOut, Ds * 0.7, 3, P, config.secondarySupplyTemp_C,
      flowPerHX_secondary, nc, rough));

    // Secondary side rejects heat to primary (negative Q = heat removed from secondary)
    const designQ = -(config.rackHeatLoad_W * config.numRackBranches / config.numPlateHX);
    // Same sizing rationale as the primary side (secondary header bore).
    components.set(hxId, new NSHeatExchanger(hxId, {
      Q_rate: designQ,
      UA: config.plateHX_UA_WK,
      hxType: 'counter',
      K_loss: 6,
      D: Ds,
    }));

    junctions.set(`j_sec_hx_${i}`, createJunction(`j_sec_hx_${i}`, 'component',
      [{ pipeId: pipeIn, faceIndex: nc, direction: 'inflow' },
       { pipeId: pipeOut, faceIndex: 0, direction: 'outflow' }],
      P, h_sec_return, hxId));
  }

  // Secondary HX merge → return to pumps
  const secHxMergeConns = [
    ...Array.from({ length: config.numPlateHX }, (_, i) =>
      ({ pipeId: `p_sec_hx_out_${i}`, faceIndex: nc, direction: 'inflow' as const })),
    { pipeId: 'p_sec_hx_to_pumps', faceIndex: 0, direction: 'outflow' as const },
  ];
  junctions.set('j_sec_hx_merge', createJunction('j_sec_hx_merge', 'merge',
    secHxMergeConns, P, h_sec_supply));

  pipes.set('p_sec_hx_to_pumps', makeCoolingPipe('p_sec_hx_to_pumps', Ds, 10,
    P, config.secondarySupplyTemp_C, totalSecondaryFlow, nc, rough));

  // Secondary pressure anchor — single boundary junction at pump suction.
  // Only ONE boundary per loop to avoid over-constraining the pressure field.
  junctions.set('j_sec_tank', createJunction('j_sec_tank', 'boundary',
    [{ pipeId: 'p_sec_hx_to_pumps', faceIndex: 0, direction: 'outflow' }],
    P, h_sec_supply));

  // HX split junction takes directly from the return header
  junctions.set('j_sec_hx_split', createJunction('j_sec_hx_split', 'split',
    [{ pipeId: 'p_sec_return_header', faceIndex: nc + 2, direction: 'inflow' as const },
     ...Array.from({ length: config.numPlateHX }, (_, i) =>
      ({ pipeId: `p_sec_hx_in_${i}`, faceIndex: 0, direction: 'outflow' as const }))],
    P, h_sec_return));

  return createNSSolverState(pipes, junctions, components, simpleConfig);
}

// ─── Thermal Coupling ────────────────────────────────────────────────────────

/**
 * Update plate HX heat transfer rates based on current temperatures on both sides.
 * This implements the thermal coupling between primary and secondary loops.
 *
 * Must be called before each SIMPLE step in the scenario runner.
 */
export function updateHXCoupling(state: NSSolverState, config: CoolingNetworkConfig): void {
  for (let i = 0; i < config.numPlateHX; i++) {
    const priHx = state.components.get(`pri_hx_${i}`);
    const secHx = state.components.get(`sec_hx_${i}`);
    if (!priHx || !secHx) continue;

    // Get temperatures from HX junction (upstream cell enthalpy → temperature)
    const priJunction = state.junctions.get(`j_pri_hx_${i}`);
    const secJunction = state.junctions.get(`j_sec_hx_${i}`);
    if (!priJunction || !secJunction) continue;

    // Primary side: cold fluid entering, secondary side: hot fluid entering
    // For counter-flow: primary enters cold (supply), secondary enters hot (return)
    const T_pri_in = enthalpyToTemp(priJunction.h, config.operatingPressure_Pa);
    const T_sec_in = enthalpyToTemp(secJunction.h, config.operatingPressure_Pa);

    // Get mass flows from connected pipes
    const mdot_pri = getJunctionMdot(state, `j_pri_hx_${i}`);
    const mdot_sec = getJunctionMdot(state, `j_sec_hx_${i}`);

    // Compute Q using ε-NTU method
    const cp = 4186; // J/(kg·K) — liquid water
    const C_pri = Math.abs(mdot_pri) * cp;
    const C_sec = Math.abs(mdot_sec) * cp;

    if (C_pri < 1 || C_sec < 1) continue; // No flow → no heat transfer

    const C_min = Math.min(C_pri, C_sec);
    const C_max = Math.max(C_pri, C_sec);
    const C_r = C_min / C_max;
    const NTU = config.plateHX_UA_WK / C_min;

    // Counter-flow effectiveness
    let epsilon: number;
    if (Math.abs(C_r - 1) < 1e-6) {
      epsilon = NTU / (1 + NTU);
    } else {
      const expTerm = Math.exp(-NTU * (1 - C_r));
      epsilon = (1 - expTerm) / (1 - C_r * expTerm);
    }
    epsilon = Math.min(epsilon, 0.95); // Physical limit

    // Q = ε × C_min × (T_hot_in - T_cold_in)
    // Secondary is hot side (return water), primary is cold side (supply water)
    const Q = epsilon * C_min * (T_sec_in - T_pri_in);

    // Apply: primary absorbs Q (positive), secondary rejects Q (negative)
    priHx.params.Q_rate = Q;
    secHx.params.Q_rate = -Q;
  }
}

/**
 * Convert enthalpy to temperature [°C] for liquid water.
 * Uses IAPWS-97 property lookup (propsFromPH_SI) for accuracy.
 * Falls back to linear approximation only if lookup fails.
 */
function enthalpyToTemp(h_Jkg: number, P_Pa: number): number {
  try {
    const props = propsFromPH_SI(P_Pa, h_Jkg);
    return props.T - 273.15; // K → °C
  } catch {
    return h_Jkg / 4186;
  }
}

/**
 * Get total absolute mass flow through a junction.
 */
function getJunctionMdot(state: NSSolverState, junctionId: string): number {
  const junction = state.junctions.get(junctionId);
  if (!junction) return 0;

  let totalIn = 0;
  for (const conn of junction.connections) {
    const pipe = state.pipes.get(conn.pipeId);
    if (!pipe) continue;
    const mdot = pipe.faces[conn.faceIndex].mdot;
    if ((conn.direction === 'inflow' && mdot > 0) || (conn.direction === 'outflow' && mdot < 0)) {
      totalIn += Math.abs(mdot);
    }
  }
  return totalIn;
}
